import { TradeReference, TradeReferenceUpdate } from '@dill/dill-shared';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import { PencilSimple, Trash, X } from 'phosphor-react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  deleteTradeReference,
  getTradeReferences,
  updateTradeReference,
} from '../../../../redux/services/tradeReferenceService';
import { openDillToast } from '../../../../utils/helpers';
import { DillColumn, DillTable } from '../../../general/Dill/DillTable';
import { DillButton } from '../../../general/Dill';

type EditableRef = {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  emails: string;
  phones: string;
};

const getEmails = (r: TradeReference) => (r.emails ?? []).map((e) => e.value).join(', ');

const getPhones = (r: TradeReference) => (r.phoneNumbers ?? []).map((p) => p.value).join(', ');

const getName = (r: TradeReference) => r.originalName ?? r.name ?? '';

const toEditable = (r: TradeReference): EditableRef => ({
  name: getName(r),
  address: r.address ?? '',
  city: r.city ?? '',
  state: r.state ?? '',
  zipCode: r.zipCode ?? '',
  emails: getEmails(r),
  phones: getPhones(r),
});

const InputField = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-9 px-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
    />
  </div>
);

const EditModal = ({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: EditableRef;
  onClose: () => void;
  onSave: (data: EditableRef) => void;
}) => {
  const [form, setForm] = useState<EditableRef>(initial);
  const [errors, setErrors] = useState<Partial<EditableRef>>({});

  useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const set = (key: keyof EditableRef) => (v: string) => {
    setForm((p) => ({ ...p, [key]: v }));
    setErrors((p) => ({ ...p, [key]: '' }));
  };

  const validate = () => {
    const e: Partial<EditableRef> = {};
    if (!form.name.trim()) e.name = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = () => {
    if (validate()) onSave(form);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-lg bg-white rounded-2xl shadow-xl flex flex-col h-auto max-h-[calc(100vh-60px)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 z-10">
          <div>
            <h3 className="text-xs font-semibold text-gray-900">Edit trade reference</h3>
            <p className="text-xs text-gray-400 mt-0.5">Update reference details below</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-4">
            <InputField
              label="Name *"
              value={form.name}
              onChange={set('name')}
              placeholder="Company or contact name"
            />
            {errors.name && <p className="text-xs text-red-500 -mt-3">{errors.name}</p>}

            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Address
                </span>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <InputField
                    label="Street"
                    value={form.address}
                    onChange={set('address')}
                    placeholder="123 Main St"
                  />
                </div>
                <InputField
                  label="City"
                  value={form.city}
                  onChange={set('city')}
                  placeholder="City"
                />
                <InputField
                  label="State"
                  value={form.state}
                  onChange={set('state')}
                  placeholder="CA"
                />
                <div className="col-span-2">
                  <InputField
                    label="Zip code"
                    value={form.zipCode}
                    onChange={set('zipCode')}
                    placeholder="90210"
                  />
                </div>
              </div>
            </div>

            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Contact
                </span>
              </div>
              <div className="p-4 flex flex-col gap-3">
                <InputField
                  label="Emails (comma-separated)"
                  value={form.emails}
                  onChange={set('emails')}
                  placeholder="a@co.com, b@co.com"
                />
                <InputField
                  label="Phone numbers (comma-separated)"
                  value={form.phones}
                  onChange={set('phones')}
                  placeholder="555-1234, 555-5678"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 shrink-0">
          <DillButton variant="secondary" label="Cancel" onClick={onClose} height={36} />
          <DillButton variant="primary" label="Save changes" onClick={handleSave} height={36} />
        </div>
      </div>
    </Modal>
  );
};

const DeleteModal = ({
  open,
  name,
  onClose,
  onConfirm,
}: {
  open: boolean;
  name: string;
  onClose: () => void;
  onConfirm: () => void;
}) => (
  <Modal open={open} onClose={onClose}>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden">
      <div className="p-6 flex flex-col gap-3">
        <div className="w-10 h-10 rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
          <Trash size={18} className="text-red-500" />
        </div>
        <div>
          <h3 className="text-xs font-semibold text-gray-900">Remove trade reference</h3>
          <p className="text-xs text-gray-500 mt-1">
            Are you sure you want to remove{' '}
            <span className="font-medium text-gray-700">{name}</span> from the pool? This cannot be
            undone.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
        <DillButton variant="secondary" label="Cancel" onClick={onClose} height={36} />
        <DillButton variant="danger" label="Remove" onClick={onConfirm} height={36} />
      </div>
    </div>
  </Modal>
);

const TradeReferencesTable = ({
  tradeReferences,
  onUploadClick,
}: {
  tradeReferences: TradeReference[];
  onUploadClick: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: any) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [editTarget, setEditTarget] = useState<TradeReference | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TradeReference | null>(null);

  const columns = useMemo<DillColumn<TradeReference>[]>(
    () => [
      {
        key: 'name',
        label: 'Name',
        width: 150,
        sortable: true,
        getSortValue: (row: TradeReference) => row.originalName ?? row.name ?? '',
        render: (_: any, row: TradeReference) => (
          <p className="text-xs capitalize w-full">{row.originalName ?? row.name}</p>
        ),
      },
      {
        key: 'address',
        label: 'Address',
        width: 200,
        sortable: true,
        getSortValue: (row: TradeReference) =>
          `${row.address ? `${row.address},` : ''} ${row.city ? `${row.city},` : ''} ${
            row.state ?? ''
          } ${row.zipCode ?? ''}`.trim(),
        render: (_: any, row: TradeReference) => (
          <p className="text-xs capitalize w-full">
            {`${row.address ? `${row.address},` : ''} ${row.city ? `${row.city},` : ''} ${
              row.state ?? ''
            } ${row.zipCode ?? ''}`.trim() || '-'}
          </p>
        ),
      },
      {
        key: 'email',
        label: 'Email',
        width: 180,
        sortable: true,
        getSortValue: (row: TradeReference) =>
          (row.emails ?? [])?.map((email) => email.value).join(', '),
        render: (_: any, row: TradeReference) => (
          <p className="text-xs w-full">
            {(row.emails ?? [])?.map((email) => email.value).join(', ') || '-'}
          </p>
        ),
      },
      {
        key: 'phone',
        label: 'Phone',
        width: 150,
        sortable: true,
        getSortValue: (row: TradeReference) =>
          (row.phoneNumbers ?? [])?.map((phone) => phone.value).join(', '),
        render: (_: any, row: TradeReference) => (
          <p className="text-xs w-full">
            {(row.phoneNumbers ?? [])?.map((phone) => phone.value).join(', ') || '-'}
          </p>
        ),
      },
      {
        key: 'action',
        label: '',
        width: 150,
        sortable: false,
        render: (_: any, row: TradeReference) => (
          <div className="flex items-center gap-2 justify-end">
            <DillButton
              variant="secondary"
              label="Edit"
              onClick={() => setEditTarget(row)}
              icon={<PencilSimple size={13} />}
              height={28}
            />
            <DillButton
              variant="icon-red"
              onClick={() => setDeleteTarget(row)}
              icon={<Trash size={13} />}
              height={28}
              width={28}
            />
          </div>
        ),
      },
    ],
    []
  );

  const handleSaveEdit = async (data: EditableRef) => {
    if (!editTarget) return;
    setEditTarget(null);

    const updateData: TradeReferenceUpdate = {
      ...(data.name && { originalName: data.name }),
      ...(data.address && { address: data.address }),
      ...(data.city && { city: data.city }),
      ...(data.state && { state: data.state }),
      ...(data.zipCode && { zipCode: data.zipCode }),
      ...(data.emails && { emails: data.emails.split(',').map((e) => ({ value: e.trim() })) }),
      ...(data.phones && {
        phoneNumbers: data.phones.split(',').map((p) => ({ value: p.trim() })),
      }),
    };

    const results = await dispatch(
      updateTradeReference({
        supplierId: currentSupplier?.id ?? '',
        tradeReferenceId: editTarget.id,
        tradeReferenceDetails: { ...updateData },
      })
    );

    if (results.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: 'Successfully updated trade reference',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to update trade reference',
      });
    }
  };

  const handleDelete = async () => {
    if (deleteTarget === null) return;
    const results = await dispatch(
      deleteTradeReference({
        supplierId: currentSupplier?.id ?? '',
        tradeReferenceId: deleteTarget.id,
      })
    );

    if (results.type.includes('fulfilled')) {
      await dispatch(getTradeReferences({ supplierId: currentSupplier?.id ?? '' }));
      openDillToast({
        type: 'SUCCESS',
        message: 'Successfully deleted trade reference',
      });
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to delete trade reference',
      });
    }
    setDeleteTarget(null);
  };

  const toolbarRight = (
    <div className="flex items-center gap-2">
      <DillButton variant="secondary" label="Upload File" onClick={onUploadClick} height={35} />
    </div>
  );

  return (
    <div className="w-full h-full">
      <DillTable
        columns={columns}
        data={tradeReferences}
        rowKey={(row) => row.id}
        sortable
        pagination
        pageSize={20}
        totalCount={tradeReferences.length}
        emptyMessage="No trade references found."
        toolbarRight={toolbarRight}
        countLabel="Trade References"
        striped={false}
        resizable
        showCount={false}
      />
      <EditModal
        open={!!editTarget}
        initial={
          editTarget
            ? toEditable(editTarget)
            : { name: '', address: '', city: '', state: '', zipCode: '', emails: '', phones: '' }
        }
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
      />
      <DeleteModal
        open={!!deleteTarget}
        name={deleteTarget?.originalName ?? deleteTarget?.name ?? ''}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default TradeReferencesTable;
